import { GoogleTagManager as GTM } from '@vpr/tracking';

import { camelize } from '@vimeo/core/libs/camelize';

import {
  CartItem,
  CartResponse,
  GtmProductDataItem,
  Picture,
  Quality,
  StockClip,
  SubscriptionDetails,
} from '../interfaces/StockGtmProduct';

interface Clip {
  clipId: number;
  link: string;
  name: string;
  pictures: Picture;
}

const PLANS = {
  plus_yearly: {
    name: 'Plus membership',
    logoUrl: 'https://f.vimeocdn.com/images_v6/stock/plus_yearly.svg',
  },
  pro: {
    name: 'Pro membership',
    logoUrl: 'https://f.vimeocdn.com/images_v6/stock/pro.svg',
  },
  business: {
    name: 'Business membership',
    logoUrl: 'https://f.vimeocdn.com/images_v6/stock/business.svg',
  },
};

/**
 * Adding events to GTM data layer
 *
 * @param {object} clipId - Id of clip added to cart
 * @param {object} cartData - Response from add to cart
 * @param {object} stocks - stock clips meta data
 * @param {number} userId
 */
function trackAddToCart(
  clipId: number,
  cartData: CartResponse,
  stocks: StockClip[],
  userId: number,
) {
  const { currencyCode } = cartData;
  const checkoutData = getCheckoutData(cartData, stocks);
  const clipData = getAddEventData(clipId, cartData, stocks);
  const actionField = getActionFieldData(cartData, userId);

  if (clipData) {
    const addEventDetail = {
      ecommerce: {
        add: {
          currencyCode,
          products: [clipData],
        },
      },
    };
    GTM.addData(addEventDetail);
  }

  const checkoutEventDetail = {
    ecommerce: {
      currencyCode,
      checkout: {
        actionField,
        products: checkoutData,
      },
    },
  };
  GTM.addData(checkoutEventDetail);
}

/**
 * Builds and returns checkout data
 *
 * @param {object} cartData - Response from add to cart
 * @param {array} stocks - Additional stocks meta data
 */
const getCheckoutData = (cartData: CartResponse, stocks: StockClip[]) => {
  const checkoutData: GtmProductDataItem[] = [];
  const { items, subscriptionDetails, discountApplied } = cartData;

  if (subscriptionDetails && subscriptionDetails.subscriptionAdded) {
    checkoutData.push(getSubscriptionDetails(subscriptionDetails));
  }

  items.forEach((item: CartItem) => {
    const metadata = stocks.find((meta) => meta.clipId === item.clipId);
    if (metadata) {
      checkoutData.push(formatStockForCartEvents(item, discountApplied, metadata));
    }
  });
  return checkoutData;
};

/**
 * Builds and returns add event data
 *
 * @param {object} clipId - Id of clip added to cart
 * @param {object} cartData - Response from add to cart
 * @param {array} stocks - Additional stock meta data
 */
const getAddEventData = (clipId: number, cartData: CartResponse, stocks: StockClip[]) => {
  const { items, discountApplied } = cartData;
  const addedItemMeta = stocks.find((meta) => meta.clipId === clipId);
  /*
   * We add a clip of highest available quality when we add from here.
   * But the cart at that time can have the same clip present in the other quality as well
   * which will be returned in the items map.
   * For the add ecommerce event, product data contains addedClip which is being identified here.
   * If the qualities array of the clip has more than 1 quality,
   * we added 4K otherwise HD. Its based on the knowledge that we have HD quality present as well,
   * for the clips that have 4K quality.
   */
  if (addedItemMeta) {
    const addedItem = items.find((item: CartItem) => {
      return (
        item.clipId === clipId &&
        item.quality === (addedItemMeta.qualities.length > 1 ? '4K' : 'HD')
      );
    });
    return addedItem && formatStockForCartEvents(addedItem, discountApplied, addedItemMeta);
  }
  return null;
};

/**
 * Preprocessing clip for adding to GTM
 *
 * @param {object} cartResponseItem - Add to cart response object
 * @param {boolean} discountApplied - If discount is applied
 * @param {object} clipDetails - Clip object
 */
const formatStockForCartEvents = (
  cartResponseItem: CartItem,
  discountApplied: boolean,
  clipDetails: StockClip,
) => {
  const { clipId, title, pictures, qualities, pageUri } = clipDetails;
  const { discountedDisplayPrice, originalDisplayPrice, quality, productId } = cartResponseItem;
  const { link } = pictures.sizes[0];
  const thumbnailUrl = `${link.split('_')[0]}.jpg`;
  const addedQuality = qualities.find((q: Quality) => q.format === quality);

  return {
    clip_id: clipId,
    clip_url: `${(window as any).origin}${pageUri}`,
    description: addedQuality
      ? `${addedQuality.format} ${addedQuality.dimension} ${addedQuality.codec}`
      : quality,
    dimension21: title,
    id: productId,
    name: 'Stock',
    price: discountApplied ? discountedDisplayPrice : originalDisplayPrice,
    price_discount: discountedDisplayPrice,
    price_original: originalDisplayPrice,
    quantity: 1,
    stride_name: title,
    thumbnail: thumbnailUrl,
    variant: addedQuality && addedQuality.format,
  };
};

/**
 * Preprocessing subscription for adding to GTM
 *
 * @param {object} subscriptionDetails - Subscription details
 */
const getSubscriptionDetails = (subscriptionDetails: SubscriptionDetails) => {
  const { subscriptionType, subscriptionPricings } = subscriptionDetails;

  const { discountedDisplayPrice, originalDisplayPrice } =
    subscriptionPricings[camelize<string>(subscriptionType)];
  return {
    name: subscriptionType,
    price_discount: discountedDisplayPrice,
    price_original: originalDisplayPrice,
    description: PLANS[subscriptionType].name,
    thumbnail: PLANS[subscriptionType].logoUrl,
  };
};

/**
 * Fetches action specific data for GTM checkout event
 *
 * @param {object} cartData - Response from add to cart
 * @param {number} userId
 */
const getActionFieldData = (cartData: CartResponse, userId: number) => {
  const { items, pricingInfo } = cartData;
  const { subtotalDisplay, totalDisplay, totalDiscountDisplay } = pricingInfo;
  const addedClipIds = items.map((item: CartItem) => item.clipId);
  const now = new Date().getTime();
  return {
    affiliation: 'stock',
    membership_discount: totalDiscountDisplay,
    sub_total: subtotalDisplay,
    total: totalDisplay,
    clips_added: items.length,
    cart_clip_ids: addedClipIds,
    id: userId ? `${userId}_${now}` : now,
  };
};

/**
 * GTM tracking event for stock preview download
 *
 * @param {object} clipData - Clip details for creating GTM product JSON
 */
const trackStockDownloadPreview = (clipData: Clip) => {
  const { clipId, name, pictures } = clipData;
  GTM.addData({
    downloadStockClipPreview: {
      products: [
        {
          clip_id: clipId,
          clip_url: `${window.location.origin}/${clipId}`,
          stride_name: name,
          thumbnail: `${pictures.sizes[0].link.split('_')[0]}.jpg`,
        },
      ],
    },
  });
};

export { trackAddToCart, trackStockDownloadPreview };
